import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DevicesAllocatedContentSharedComponent } from 'src/app/pages-content-shared/provisioning-content-shared/devices-allocated-content-shared/devices-allocated-content-shared.component';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { Hosts_DevicesAllocatedContentSharedComponent } from 'src/app/core/consts';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { Subject } from 'rxjs';

const TOP_SIMS_LIMIT=10;

@Component({
  selector: 'app-widget-row-usage-dashboard-top-sims',
  templateUrl: './widget-row-usage-dashboard-top-sims.component.html',
  styleUrls: ['./widget-row-usage-dashboard-top-sims.component.scss']
})

export class WidgetRowUsageDashboardTopSimsComponent implements OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() useContainer       : boolean | undefined;

  @Output() companyDataSaved: EventEmitter<void>=new EventEmitter<void>();
  @Output() containerClosed : EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('devicesAllocated', { static: false }) devicesAllocated!: DevicesAllocatedContentSharedComponent;


  get gridInstance (): any     { return this.devicesAllocated?.gridView?.dataGrid?.instance; }
  get hasSearchTerm(): boolean { return (this.searchTerm ?? "").trim().length>0            ; }

  private destroy$=new Subject<void>();

  public expandContainer  : boolean=true;
  public hostName         : string=Hosts_DevicesAllocatedContentSharedComponent.UsageDashboardComponent;
  public searchTerm       : string="";
  public topNLimit        : number | undefined=TOP_SIMS_LIMIT;
  public usageSortColumn  : string="orderSpendDTO.usage";
  public viewAllExpanded  : boolean=false;
  public visibleContainer : boolean=true;

  get gridHasColumnFilters(): boolean {
    try {
      const instance=this.gridInstance;

      if (!instance)
        return false;

      // Check filterValue (singular) — set by the filter row inputs and by header filters.
      // Avoids picking up filterValues (plural) which the LIVE/CEASED tab applies at the
      // system level to the status column.
      const colCount=<number>instance.columnCount();

      for (let i=0; i<colCount; i++) {
        const filterValue=instance.columnOption(i, "filterValue");

        if (filterValue!==undefined && filterValue!==null && filterValue!=="")
          return true;
      }

      return false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  get gridSortColumnCaption(): string | undefined {
    try {
      const instance=this.gridInstance;

      if (!instance)
        return undefined;

      const colCount=<number>instance.columnCount();

      for (let i=0; i<colCount; i++) {
        if (instance.columnOption(i, "sortIndex")===0 && instance.columnOption(i, "sortOrder"))
          return <string>instance.columnOption(i, "caption");
      }

      return undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }

  get gridSortIsDefault(): boolean {
    try {
      const instance=this.gridInstance;

      if (!instance)
        return true;

      const colCount=<number>instance.columnCount();

      for (let i=0; i<colCount; i++) {
        if (instance.columnOption(i, "sortIndex")===0) {
          const dataField=instance.columnOption(i, "dataField");
          const sortOrder=instance.columnOption(i, "sortOrder");

          return dataField===this.usageSortColumn && sortOrder==="desc";
        }
      }

      return false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return true;
    }
  }

  get headerSubtitle(): string {
    if (this.hasSearchTerm)
      return "Showing matches for \""+this.searchTerm.trim()+"\"";

    if (this.gridHasColumnFilters)
      return "Showing filtered results";

    if (!this.gridSortIsDefault)
      return "Custom sort applied";

    if (this.viewAllExpanded)
      return "Showing all allocated SIMs";

    return "Highest data consumption this month";
  }

  get headerTitle(): string {
    if (this.hasSearchTerm || this.gridHasColumnFilters)
      return "Search Results";

    if (!this.gridSortIsDefault)
      return "SIMs ordered by "+(this.gridSortColumnCaption ?? "");

    if (this.viewAllExpanded)
      return "All SIMs";

    return "Top 10 SIMs by Usage";
  }

  get horizontalScrollOn(): boolean {
    try {
      const checkbox=this.devicesAllocated?.dgProperties?.checkbox0;

      if (!checkbox)
        return false;

      return checkbox.value===checkbox.onValue;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private exceptionsService: ExceptionsService
  ) {

  }

  public ngOnInit(): void {
    try {

    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Public Methods

  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      // Embedded DevicesAllocatedContentSharedComponent reads its own initial filter from
      // dropdownService.trackerCompanyId / curUser.companyId during its async ngOnInit, so
      // pushing filter changes here while it's still booting causes a timing crash.
      // Forward only after it has fully initialised (gotDgProperties === true).
      if (!this.devicesAllocated?.gotDgProperties)
        return;

      if (filterCompanyID!==undefined && filterCompanyID!==null)
        this.devicesAllocated.companyDropDownOnChange(filterCompanyID);

      if (filterDate)
        this.devicesAllocated.dateFilterChanged(filterDate);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public exportAllExcelClick(): void {
    try {
      this.devicesAllocated?.triggerExport("xlsx", false);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public exportAllPdfClick(): void {
    try {
      this.devicesAllocated?.triggerExport("pdf", false);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public exportSelectedExcelClick(): void {
    try {
      this.devicesAllocated?.triggerExport("xlsx", true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public exportSelectedPdfClick(): void {
    try {
      this.devicesAllocated?.triggerExport("pdf", true);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public filterColumnsClick(): void {
    try {
      this.devicesAllocated?.toggleFilterRow();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public horizontalScrollChange(event: Event): void {
    try {
      const checkbox=this.devicesAllocated?.dgProperties?.checkbox0;

      if (!checkbox)
        return;

      const checked=(<HTMLInputElement>event.target).checked;
      const value  =checked ? checkbox.onValue : checkbox.offValue;

      this.devicesAllocated?.onCheckboxChange(value);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public searchInput(): void {
    try {
      this.devicesAllocated?.triggerSearch(this.searchTerm);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public manageColumnsClick(event?: MouseEvent): void {
    try {
      this.devicesAllocated?.triggerColumnChooser(<HTMLElement>event?.currentTarget);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public viewAllClick(): void {
    try {
      this.viewAllExpanded=!this.viewAllExpanded;
      this.topNLimit      =this.viewAllExpanded ? undefined : TOP_SIMS_LIMIT;

      this.devicesAllocated?.setTopNLimit(this.topNLimit);

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
